import React, { useState, useCallback, useEffect } from 'react';

interface TextEditorProps {
  content: string;
  fileName: string;
  onSave?: (content: string) => void;
  isLoading?: boolean;
  readonly?: boolean;
}

export const TextEditor: React.FC<TextEditorProps> = ({
  content,
  fileName,
  onSave,
  isLoading = false,
  readonly = false
}) => {
  const [editedContent, setEditedContent] = useState(content);
  // Sync local editor content when parent content prop changes
  useEffect(() => {
    setEditedContent(content);
    setHasChanges(false);
  }, [content]);
  const [hasChanges, setHasChanges] = useState(false);

  const handleContentChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newContent = e.target.value;
    setEditedContent(newContent);
    setHasChanges(newContent !== content);
  }, [content]);

  const handleSave = useCallback(() => {
    if (onSave && hasChanges) {
      onSave(editedContent);
      setHasChanges(false);
    }
  }, [onSave, editedContent, hasChanges]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.ctrlKey && e.key === 's') {
      e.preventDefault();
      handleSave();
    }
  }, [handleSave]);

  return (
    <div className="text-editor h-full flex flex-col">
      <div className="flex items-center justify-between p-4 border-b bg-gray-50">
        <div className="flex items-center gap-2">
          <span className="text-lg">📄</span>
          <h3 className="font-medium text-gray-900">{fileName}</h3>
          {hasChanges && (
            <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
              Modified
            </span>
          )}
        </div>
        
        {!readonly && onSave && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Ctrl+S to save</span>
            <button
              onClick={handleSave}
              disabled={!hasChanges || isLoading}
              className={`px-3 py-1 text-sm rounded ${
                hasChanges && !isLoading
                  ? 'bg-blue-500 text-white hover:bg-blue-600'
                  : 'bg-gray-200 text-gray-500 cursor-not-allowed'
              }`}
            >
              {isLoading ? 'Saving...' : 'Save'}
            </button>
          </div>
        )}
      </div>

      <div className="flex-1 p-4">
        <textarea
          value={editedContent}
          onChange={handleContentChange}
          onKeyDown={handleKeyDown}
          readOnly={readonly}
          className={`w-full h-full resize-none border rounded font-mono text-sm ${
            readonly 
              ? 'bg-gray-50 text-gray-700 cursor-not-allowed' 
              : 'bg-white text-gray-900 focus:border-blue-500 focus:ring-1 focus:ring-blue-500'
          }`}
          style={{ 
            minHeight: '400px',
            lineHeight: '1.5'
          }}
          placeholder={readonly ? 'File content...' : 'Start typing...'}
        />
      </div>

      {!readonly && (
        <div className="p-4 border-t bg-gray-50 text-xs text-gray-500">
          <div className="flex justify-between">
            <span>Lines: {editedContent.split('\n').length}</span>
            <span>Characters: {editedContent.length}</span>
          </div>
        </div>
      )}
    </div>
  );
};